import { useCallback, useMemo, useState } from "react"
import { enqueueSnackbar } from "notistack"
import { Grid } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useNavigate } from "react-router-dom"
import { renderRowActionMenuItems } from "../components/tableElements/renderRowActionMenuItems"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { MerchantProductsQuery, fetchProducts, updateStock } from "../productsApi"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useMerchant } from "../../../../merchants/merchantHooks"
import {
    DefaultPreviewProductImageStateType,
    PreviewProductImage,
    defaultPreviewProductImageState,
} from "../components/tableElements/PreviewProductImage"
import {
    DefaultConfirmModalStateType,
    DeleteProductDialog,
    defaultConfirmModalState,
} from "../components/tableElements/DeleteProductDialog"
import { transformProductsDataWithSortOrder } from "../helpers"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { MRT_ColumnDef } from "material-react-table"
import { ProductStockInfo, ProductTableRow, UpdateStockPayload } from "../productsTypes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { COLUMN_SIZE } from "../../../../../shared/constants/table/config"
import { ProductGroupItemBox, ProductName } from "../ProductTableColumns"
import { StockPopover } from "../components/stockManagement/StockPopover"
import { useExportProducts } from "../hooks/useExportProducts"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"

const useStyles = makeStyles()((theme) => ({
    hideRowActions: {
        "& .MuiIconButton-root": {
            display: "none",
        },
    },
    deleteItem: {
        color: theme.palette.tailwind.red[600],
    },
}))

type StockPopoverState = Readonly<{
    anchorEl: HTMLElement | null
    productId: number | null
    stock: ProductStockInfo | null
}>

const defaultStockPopoverState: StockPopoverState = {
    anchorEl: null,
    productId: null,
    stock: null,
}

type AllProductsTabProps = Readonly<{
    priceListId: number
}>

export function AllProductsTab({ priceListId }: AllProductsTabProps) {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()

    const [confirmModalState, setConfirmModalState] = useState<DefaultConfirmModalStateType>(defaultConfirmModalState)

    const [previewImage, setPreviewImage] = useState<DefaultPreviewProductImageStateType>(
        defaultPreviewProductImageState,
    )

    const [stockPopoverState, setStockPopoverState] = useState<StockPopoverState>(defaultStockPopoverState)

    const products = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: {
            merchantId: merchantId,
            priceListId,
        },
        select: transformProductsDataWithSortOrder,
    })

    const { mutateAsync: callUpdateStock } = useApiMutation(updateStock, {
        async onSuccess(_params, _data, context) {
            enqueueSnackbar({
                variant: "success",
                message: l10n.getString("product-stock-update-success"),
            })
            // to prevent popover shifting on table refresh
            setTimeout(() => {
                context.queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            }, 300)
        },
    })

    const handleStockChipClick = useCallback(
        (event: React.MouseEvent<HTMLElement>, productId: number) => {
            const productRow = products.data?.find((row) => row.type === "product" && row.id === productId)
            if (productRow?.type === "product" && productRow.stock) {
                setStockPopoverState({
                    anchorEl: event.currentTarget,
                    productId,
                    stock: productRow.stock,
                })
            }
        },
        [products.data],
    )

    const handleStockUpdate = useCallback((payload: UpdateStockPayload) => callUpdateStock(payload), [callUpdateStock])

    const exportProducts = useExportProducts({ data: products.data })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(products)

    const columns = useMemo<MRT_ColumnDef<ProductTableRow>[]>(
        () => [
            columnFactory.nameColumn({
                accessorKey: "name",
                header: l10n.getString("products-name-column"),
                size: COLUMN_SIZE.L,
                Cell: ({ row }) => {
                    if (row.original.type === "group") {
                        return <ProductGroupItemBox productGroup={row.original} />
                    }

                    if (row.original.type === "product") {
                        return (
                            <ProductName
                                product={row.original}
                                onProductPhotoClick={(imageUrl) => {
                                    setPreviewImage({ show: true, image: imageUrl })
                                }}
                                onStockChipClick={handleStockChipClick}
                                activeStockProductId={stockPopoverState.productId}
                            />
                        )
                    }

                    return ""
                },
            }),
            columnFactory.moneyColumn(
                {
                    id: "price",
                    header: l10n.getString("products-price-column"),
                    size: COLUMN_SIZE.SS,
                    maxSize: COLUMN_SIZE.SS,
                },
                (row) => (row.type === "group" ? undefined : row.price),
                true,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "status",
                    header: l10n.getString("products-status-column"),
                    size: COLUMN_SIZE.XXS,
                    maxSize: COLUMN_SIZE.XXS,
                },
                (row) => row.isActive ?? false,
                {
                    cellOverride: (row) => (row.type === "group" ? "" : null),
                },
            ),
        ],
        [l10n, columnFactory, setPreviewImage, handleStockChipClick, stockPopoverState.productId],
    )

    return (
        <Grid container direction="column">
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                muiTopToolbarProps={{ sx: { minHeight: "4rem" } }}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardExportButton onClick={() => exportProducts()} />
                    </StandardTableFilter>
                )}
                renderRowActionMenuItems={({ row, closeMenu }) =>
                    renderRowActionMenuItems({
                        row,
                        closeMenu,
                        setConfirmModalState,
                        navigate,
                        errorClasses: classes.deleteItem,
                    })
                }
                muiTableBodyRowProps={({ row }) => ({
                    className: row.original.type === "group" ? classes.hideRowActions : "",
                })}
            />

            {/* Confirm delete */}
            {confirmModalState.showConfirmModal && (
                <DeleteProductDialog
                    confirmModalState={confirmModalState}
                    setConfirmModalState={setConfirmModalState}
                />
            )}

            {/* Product preview */}
            {previewImage.show && <PreviewProductImage previewImage={previewImage} setPreviewImage={setPreviewImage} />}

            {/* Stock popover */}
            {stockPopoverState.anchorEl && stockPopoverState.productId && stockPopoverState.stock && (
                <StockPopover
                    anchorEl={stockPopoverState.anchorEl}
                    onClose={() => setStockPopoverState(defaultStockPopoverState)}
                    productId={stockPopoverState.productId}
                    currentStock={stockPopoverState.stock}
                    onStockUpdate={handleStockUpdate}
                />
            )}
        </Grid>
    )
}
